/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo} from 'react';
import {Box, CardHeader, Divider, makeStyles} from '@material-ui/core';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {BaseAsset} from '@ion/core/src/types/entities';
import {getUserUnit} from '@ion/core/src/units/helpers';
import {Unit} from '@ion/core/src/units/units';
import {isTrue} from '@ion/core/src/utils/BooleanUtils';
import {genericUnitConversion} from '@ion/core/src/utils/Conversions';
import {toFixed} from '@ion/core/src/utils/NumberUtil';
import {TemperatureCell} from '../../components/grid/TemperatureCell';
import {SCIDeviceSettingsUnits} from '../../editors/DeviceEditorUtils';
import CardItemRow from '../../layout/CardItemRow';
import {dlsColor, dlsTypography} from '../../theme/theme';
import {useThemeMode} from '../../theme/useThemeContext';
import {getFormattedDate} from '../../utils/ComponentUtils';
import ContentLabel from './ContentLabel';

export interface SCDeviceEventPopupContentProps {
  selectedAsset: BaseAsset;
};

const useStyles = makeStyles(() => ({
  title : {
    ...dlsTypography('tabs'),
    fontSize     : '12px',
    marginLeft   : '8px',
    padding      : '2px 0',
    color        : dlsColor('white'),
    overflow     : 'hidden',
    textOverflow : 'ellipsis'
  },
  deviderRoot : {
    height          : '1px !important',
    backgroundColor : dlsColor('grey5')
  },
  titleRoot : {
    width           : '100%',
    padding         : '0 !important',
    backgroundColor : dlsColor('grey12'),
    borderRadius    : '4px 4px 0 0'
  },
  boxRoot : {
    padding : 0,
    width   : '-webkit-fill-available'
  },
  container : {
    display       : 'flex',
    flexDirection : 'column',
    padding       : '0 8px'
  }
}));

/**
 * @SCDeviceEventPopupContent
 *
 * The SC device Event Popup provides a component which allows users to show some
 * CDM/shipment information when hovering on an event on the History Layer.
 *
 *
 * ```typescript
 *
 *  <SCDeviceEventPopupContent asset={selectedAsset} />
 *
 * ```
 */
export const SCDeviceEventPopupContent = ({selectedAsset}: SCDeviceEventPopupContentProps) => {
  const classes       = useStyles();
  const {mode}        = useThemeMode();
  const units         = getUserUnit() || Unit.US;
  const systemOfUnits = {value: units};
  const {
    assetName,
    deviceName,
    eventTime,
    shockEvent,
    tiltEvent,
    temperatureLevelValue,
    temperatureLevelUnits,
    temperatureBreach,
    maxTemperatureThreshold,
    minTemperatureThreshold,
    lightLevelValue,
    lightLevelUnits,
    lightBreach,
    minLightThreshold,
    maxLightThreshold,
    tiltBreach,
    shockBreach,
    isShockTiltAllowed
  } = selectedAsset || {};
  const temperature = genericUnitConversion(temperatureLevelValue, temperatureLevelUnits, systemOfUnits, 2);

  const getItemValue = useCallback((value, unit) => {
    if (isTrue(isShockTiltAllowed)) {
      return parseInt(value) ? `${toFixed(value, 2)}${unit}` : '-';
    }
    return value;
  }, [isShockTiltAllowed]);

  const showItem = useCallback((value) => {
    return isTrue(isShockTiltAllowed) ?? (!isUndefined(value) && value !== 0);
  }, [isShockTiltAllowed]);

  const styles = useMemo(() => ({
    fontSize      : '10px',
    textTransform : 'uppercase',
    fontWeight    : '500'
  }), []);

  const getValueStyle = useCallback((breached = false) => {
    const styles: any = {textAlign: 'right', fontSize: '10px', fontWeight: '500'};
    if (isTrue(breached)) {
      styles.color = dlsColor('caRed');
    }
    return styles;
  }, []);

  const temperatureStyle = useMemo(() => ({
    marginRight : '6px',
    fontSize    : '10px',
    color       : dlsColor('grey2'),
    fontWeight  : '500'
  }), [mode]);

  const lightValue = useMemo(() => {
    let val = `${lightLevelValue}${lightLevelUnits}`;
    if (isTrue(lightBreach)) {
      const max = parseInt(maxLightThreshold as string);
      const min = parseInt(minLightThreshold as string);
      const current = parseInt(lightLevelValue);
      const result = current > max ? current - max : current - min;
      const diff = result > 0 ? `+${result}` : `${result}`;
      val = `${val} (${diff} ${lightLevelUnits})`;
    }
    return val;
  }, [
    lightLevelValue,
    lightLevelUnits,
    minLightThreshold,
    maxLightThreshold,
    lightBreach
  ]);

  return (
    <Box p={2} className={classes.boxRoot}>
      <CardHeader title={assetName || deviceName} classes={{root: classes.titleRoot, title: classes.title}} />
      <Divider classes={{root: classes.deviderRoot}} />
      <div className={classes.container}>
        <CardItemRow isCenterAligned={false}
          label={(
            <ContentLabel label={`${translate('maps:history.legendByAsset.popUp.time')}:`}
              styles={styles} />)}
          value={(
            <ContentLabel label={getFormattedDate(eventTime, true)}
              styles={getValueStyle()} />
          )} />
        <CardItemRow isCenterAligned={false}
          label={(
            <ContentLabel label={`${translate('maps:history.legendByAsset.popUp.temperature')}:`}
              styles={styles} />)}
          value={(
            <TemperatureCell value={temperature}
              uom={units}
              inputUnit={temperatureLevelUnits}
              maxTemperatureThreshold={maxTemperatureThreshold}
              minTemperatureThreshold={minTemperatureThreshold}
              style={temperatureStyle}
              breached={temperatureBreach}
              showDiffs />
          )} />
        {showItem(shockEvent) && (
          <CardItemRow isCenterAligned={false}
            label={(
              <ContentLabel label={`${translate('maps:history.legendByAsset.popUp.shock')}:`}
                styles={styles} />)}
            value={(
              <ContentLabel label={getItemValue(shockEvent, SCIDeviceSettingsUnits.Shock)}
                styles={getValueStyle(shockBreach)} />
            )} />
        )}
        {showItem(tiltEvent) && (
          <CardItemRow isCenterAligned={false}
            label={(
              <ContentLabel label={`${translate('maps:history.legendByAsset.popUp.tilt')}:`}
                styles={styles} />)}
            value={(
              <ContentLabel label={getItemValue(tiltEvent, SCIDeviceSettingsUnits.Degree)}
                styles={getValueStyle(tiltBreach)} />
            )} />
        )}
        {lightLevelValue && (
          <CardItemRow isCenterAligned={false}
            label={(
              <ContentLabel label={`${translate('maps:history.legendByAsset.popUp.light')}:`}
                styles={styles} />)}
            value={(
              <ContentLabel label={lightValue}
                styles={getValueStyle(lightBreach)} />
            )} />
        )}
      </div>
    </Box>
  );
};

export default SCDeviceEventPopupContent;
